import React, { useRef, createContext, ReactNode, useEffect } from 'react';

// Handles sequential audio announcements with a max queue of 15
interface AudioControlContextProps {
    playAudio: (url: string, onEnd: () => void) => Promise<void>;
    stopAudio: () => void;
}

// Create a context
export const AudioControlContext = createContext<
    AudioControlContextProps | undefined
>(undefined);

// Create a provider
interface AudioControllerProviderProps {
    children: ReactNode;
}

const MAX_QUEUE_SIZE = 15; 

export function AudioControllerProvider({
    children,
}: AudioControllerProviderProps): JSX.Element {
    const audioContextRef = useRef<AudioContext | null>(null);
    const audioBufferSourceRef = useRef<AudioBufferSourceNode | null>(null);
    const audioQueueRef = useRef<{ url: string; onEnd: () => void }[]>([]);
    const isPlayingRef = useRef(false);

    // Stops current audio and resets state
    const cleanup = () => {
        if (audioBufferSourceRef.current) {
            try {
                audioBufferSourceRef.current.stop();
            } catch (e) {}
            audioBufferSourceRef.current = null;
        }
        isPlayingRef.current = false;
    };

    useEffect(() => {
        return () => {
            cleanup();
            audioContextRef.current?.close();
        };
    }, []);

    // Process and play next audio in queue
    const playNextInQueue = async () => {
        if (audioQueueRef.current.length === 0 || isPlayingRef.current) {
            return;
        }

        isPlayingRef.current = true;
        const { url, onEnd } = audioQueueRef.current[0];
        audioQueueRef.current = audioQueueRef.current.slice(1);

        try {
            // Init audio context if needed (browser requirement)
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext ||
                    //@ts-ignore
                    window.webkitAudioContext)();
            }

            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

            // Setup and start audio playback
            audioBufferSourceRef.current = audioContextRef.current.createBufferSource();
            audioBufferSourceRef.current.buffer = audioBuffer;
            audioBufferSourceRef.current.connect(audioContextRef.current.destination);
            
            // When audio finishes, cleanup and play next
            audioBufferSourceRef.current.onended = () => {
                cleanup();
                onEnd();
                playNextInQueue();
            };

            audioBufferSourceRef.current.start();
        } catch (error) {
            cleanup();
            onEnd();
            playNextInQueue();
        }
    };

    // Stop current audio and clear queue
    function stopAudio() {
        cleanup();
        audioQueueRef.current = [];
    }

    // Add new audio to queue, drop oldest if full
    const playAudio = async (url: string, onEnd: () => void) => {
        if (audioQueueRef.current.length >= MAX_QUEUE_SIZE) {
            audioQueueRef.current = audioQueueRef.current.slice(-MAX_QUEUE_SIZE + 1);
        }
        audioQueueRef.current.push({ url, onEnd });
        playNextInQueue();
    };

    return (
        <AudioControlContext.Provider value={{ playAudio, stopAudio }}>
            {children}
        </AudioControlContext.Provider>
    );
}
